'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { usePathname } from 'next/navigation';

import { useTranslation } from 'react-i18next';

import { CONSENT_BANNER } from '~/(marketing)/_lib/consent.content';
import { QuickLeadPrompt } from '~/(marketing)/_components/lead-capture/quick-lead-prompt';
import { WhatsAppChatButton } from '~/(marketing)/_components/lead-capture/whatsapp-chat-button';
import {
  ATTRIBUTION_COOKIE,
  attributionCookie,
  buildFbc,
  parseAttribution,
  readAttribution,
} from '~/lib/marketing/tracking/attribution';
import {
  MARKETING_TRACKING,
  type MarketingBrowserEvent,
  isViewContentPath,
} from '~/lib/marketing/tracking/config';
import { isAskflowSalesHost, isWhiteLabelBuild } from '~/lib/marketing/lead-capture/whatsapp-chat';
import {
  MARKETING_CONSENT_COOKIE,
  type MarketingConsent,
  marketingConsentCookie,
  parseMarketingConsent,
} from '~/lib/marketing/tracking/consent';

import { readCookie } from './browser-cookies';
import {
  MarketingTrackingContext,
  type MarketingTrackingValue,
  useMarketingTracking,
} from './marketing-tracking-context';

export { useMarketingTracking };

type Gtag = (...args: unknown[]) => void;
type Fbq = (...args: unknown[]) => void;

declare global {
  interface Window {
    fbq?: Fbq;
    gtag?: Gtag;
    dataLayer?: unknown[];
  }
}

const { metaPixelId, ga4MeasurementId, googleAdsId, googleAdsLeadLabel, linkedInPartnerId } =
  MARKETING_TRACKING;

// Consent lives in a cookie; this tiny store lets every hook re-render when
// the banner writes a new choice.
const consentListeners = new Set<() => void>();

function subscribeConsent(listener: () => void) {
  consentListeners.add(listener);
  // A choice made in another tab only shows up in this tab's cookie, so
  // re-read it whenever the visitor comes back to this tab.
  window.addEventListener('focus', listener);
  document.addEventListener('visibilitychange', listener);

  return () => {
    consentListeners.delete(listener);
    window.removeEventListener('focus', listener);
    document.removeEventListener('visibilitychange', listener);
  };
}

function getConsentSnapshot(): string {
  return readCookie(MARKETING_CONSENT_COOKIE) ?? '';
}

function getServerConsentSnapshot(): string | null {
  return null;
}

const noopSubscribe = () => () => undefined;

const WHITE_LABEL_BUILD = isWhiteLabelBuild(process.env.NEXT_PUBLIC_DEFAULT_LANDING_PAGE);

function getSalesHostSnapshot(): boolean {
  return isAskflowSalesHost(window.location.host, WHITE_LABEL_BUILD);
}

function getServerSalesHostSnapshot(): boolean {
  return false;
}

function saveConsent(consent: MarketingConsent) {
  document.cookie = marketingConsentCookie(consent);
  consentListeners.forEach((listener) => listener());
}

const MARKETING_TAG_HOSTS = new Set(['askflow.sa', 'www.askflow.sa']);

/** Previews, QA and local hosts must not feed the production ad datasets. */
function isMarketingTagHost(): boolean {
  return MARKETING_TAG_HOSTS.has(window.location.host);
}

const VENDOR_COOKIE_PATTERN = /^(_fbp|_fbc|_ga|_ga_.+|_gid|_gcl_.+|li_fat_id|lidc|bcookie|_uetsid|_uetvid)$/;

/** Expires the ad and analytics cookies set by the tags after a withdrawal. */
function clearVendorCookies() {
  const domains = ['', `; Domain=${window.location.hostname}`, `; Domain=.${window.location.hostname.replace(/^www\./, '')}`];

  for (const name of document.cookie.split('; ').map((part) => part.split('=')[0] ?? '')) {
    if (!VENDOR_COOKIE_PATTERN.test(name)) continue;

    for (const domain of domains) {
      document.cookie = `${name}=; Path=/; Max-Age=0${domain}`;
    }
  }
}

function loadScript(src: string) {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

/*
 * Each installer defines the vendor's documented queue stub synchronously and
 * then loads the SDK, so events tracked in the same commit are queued rather
 * than lost. They run at most once per page load.
 */
let googleInstalled = false;
let metaInstalled = false;
let linkedInInstalled = false;

function installGoogleTag(advertising: boolean) {
  const adsConsent = advertising ? 'granted' : 'denied';

  if (googleInstalled) {
    window.gtag?.('consent', 'update', {
      ad_storage: adsConsent,
      ad_user_data: adsConsent,
      ad_personalization: adsConsent,
    });
    if (advertising) window.gtag?.('config', googleAdsId);
    return;
  }

  googleInstalled = true;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // gtag.js reads the `arguments` object itself, not an array copy.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: adsConsent,
    ad_user_data: adsConsent,
    ad_personalization: adsConsent,
  });
  window.gtag('js', new Date());
  window.gtag('config', ga4MeasurementId);
  if (advertising) window.gtag('config', googleAdsId);
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${ga4MeasurementId}`);
}

function installMetaPixel() {
  if (metaInstalled) return;
  metaInstalled = true;

  type FbqStub = Fbq & { callMethod?: Fbq; queue: unknown[]; push: Fbq; loaded: boolean; version: string };
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as FbqStub;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  window.fbq('init', metaPixelId);
  loadScript('https://connect.facebook.net/en_US/fbevents.js');
}

function installLinkedInInsightTag() {
  if (linkedInInstalled) return;
  linkedInInstalled = true;

  const linkedInWindow = window as Window & { _linkedin_data_partner_ids?: string[] };
  linkedInWindow._linkedin_data_partner_ids = [
    ...(linkedInWindow._linkedin_data_partner_ids ?? []),
    linkedInPartnerId,
  ];
  loadScript('https://snap.licdn.com/li.lms-analytics/insight.min.js');
}

function sendServerMirror(eventName: MarketingBrowserEvent, eventId: string) {
  const body = JSON.stringify({ eventName, eventId, url: window.location.href });

  void fetch('/api/marketing/events', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

export function MarketingTrackingProvider({ children }: React.PropsWithChildren) {
  const rawConsent = useSyncExternalStore<string | null>(
    subscribeConsent,
    getConsentSnapshot,
    getServerConsentSnapshot,
  );
  const consent = rawConsent === null ? null : parseMarketingConsent(rawConsent);
  const decided = rawConsent !== null && consent !== null;
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The lead prompt and the consent banner never stack; the banner returns
  // once the prompt closes.
  const [leadPromptOpen, setLeadPromptOpen] = useState(false);
  const pathname = usePathname();
  const salesHost = useSyncExternalStore(noopSubscribe, getSalesHostSnapshot, getServerSalesHostSnapshot);
  const lastTrackedPath = useRef<string | null>(null);

  // Tags only ever run on the real marketing host, never on previews.
  const tagHost = rawConsent !== null && isMarketingTagHost();
  const advertising = tagHost && (consent?.advertising ?? false);
  const analytics = tagHost && (consent?.analytics ?? false);

  const track = useCallback<MarketingTrackingValue['track']>(
    (eventName, whatsAppReference) => {
      const eventId = crypto.randomUUID();

      if (advertising) {
        const customData = whatsAppReference ? { content_name: 'whatsapp' } : {};
        window.fbq?.('track', eventName, customData, { eventID: eventId });
        sendServerMirror(eventName, eventId);
      }

      if (analytics && eventName === 'Contact') {
        if (whatsAppReference) window.gtag?.('event', 'whatsapp_click', { wa_ref: whatsAppReference });
        else window.gtag?.('event', 'demo_scheduler_open');
      }
    },
    [advertising, analytics],
  );

  const trackLead = useCallback<MarketingTrackingValue['trackLead']>(
    (eventId) => {
      if (advertising) {
        window.fbq?.('track', 'Lead', {}, { eventID: eventId });
        window.gtag?.('event', 'conversion', {
          send_to: `${googleAdsId}/${googleAdsLeadLabel}`,
          transaction_id: eventId,
        });
      }

      if (analytics) window.gtag?.('event', 'generate_lead');
    },
    [advertising, analytics],
  );

  // A withdrawal made in another tab: scripts here are still running, and
  // only a reload drops them.
  const tagsInstalled = useRef(false);

  useEffect(() => {
    if (tagsInstalled.current && !advertising && !analytics) window.location.reload();
  }, [advertising, analytics]);

  // Declared before the page-view effect so tags exist when it fires.
  useEffect(() => {
    if (analytics || advertising) tagsInstalled.current = true;
    if (analytics || advertising) installGoogleTag(advertising);
    if (advertising) {
      installMetaPixel();
      installLinkedInInsightTag();
    }
  }, [analytics, advertising]);

  // First-touch attribution is strictly necessary for crediting leads, so it
  // is recorded on the landing page before any consent choice.
  useEffect(() => {
    if (readAttribution(readCookie(ATTRIBUTION_COOKIE))) return;

    document.cookie = attributionCookie(
      parseAttribution(window.location.href, document.referrer, Date.now()),
    );
  }, []);

  // Meta's click cookie is advertising data, so it only exists after consent.
  useEffect(() => {
    if (!advertising || readCookie('_fbc')) return;

    const attribution = readAttribution(readCookie(ATTRIBUTION_COOKIE));

    if (attribution?.fbclid) {
      document.cookie = `_fbc=${buildFbc(attribution.fbclid, attribution.ts)}; Path=/; Max-Age=${90 * 24 * 60 * 60}; SameSite=Lax; Secure`;
    }
  }, [advertising]);

  // Client-side navigations do not reload the pixel, so page views are
  // reported per pathname here (GA4 tracks history changes on its own).
  useEffect(() => {
    if (!advertising || lastTrackedPath.current === pathname) return;

    lastTrackedPath.current = pathname;

    const eventId = crypto.randomUUID();
    window.fbq?.('track', 'PageView', {}, { eventID: eventId });
    sendServerMirror('PageView', eventId);

    if (isViewContentPath(pathname)) track('ViewContent');
  }, [advertising, pathname, track]);

  const value = useMemo<MarketingTrackingValue>(
    () => ({ track, trackLead, openConsentSettings: () => setSettingsOpen(true) }),
    [track, trackLead],
  );

  const bannerVisible = rawConsent !== null && !leadPromptOpen && (!decided || settingsOpen);

  const choose = (accepted: boolean) => {
    if (!accepted) clearVendorCookies();

    saveConsent({ analytics: accepted, advertising: accepted });
    setSettingsOpen(false);
  };

  return (
    <MarketingTrackingContext.Provider value={value}>
      {children}

      {bannerVisible ? <ConsentBanner onChoose={choose} /> : null}

      {salesHost ? (
        <>
          <QuickLeadPrompt consentDecided={decided} onOpenChange={setLeadPromptOpen} />
          <WhatsAppChatButton hidden={bannerVisible || leadPromptOpen} />
        </>
      ) : null}
    </MarketingTrackingContext.Provider>
  );
}

function ConsentBanner({ onChoose }: { onChoose: (accepted: boolean) => void }) {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar' : 'en';
  const c = CONSENT_BANNER;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={c.title[locale]}
      data-test="marketing-consent-banner"
      className="fixed inset-x-4 bottom-4 z-[90] mx-auto max-w-[640px] rounded-2xl border border-[#dcd7cf] bg-[#f7f6f4] p-5 text-[#202020] shadow-xl md:p-6"
    >
      <p className="font-roboto text-[16px] font-medium">{c.title[locale]}</p>
      <p className="mt-2 font-roboto text-[14px] leading-[1.5] opacity-80">
        {c.body[locale]}{' '}
        <a href="/privacy-policy" className="underline underline-offset-4">
          {c.privacy[locale]}
        </a>
      </p>
      {/* Same size and weight of text for both choices: declining must be as
          easy as accepting. Only the fill marks Accept as the brand action. */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <button
          type="button"
          data-test="marketing-consent-reject"
          onClick={() => onChoose(false)}
          className="h-11 cursor-pointer border border-[#202020] font-roboto-mono text-[13px] font-bold uppercase text-[#202020] transition-colors hover:bg-[#ede8e2]"
        >
          {c.reject[locale]}
        </button>
        <button
          type="button"
          data-test="marketing-consent-accept"
          onClick={() => onChoose(true)}
          className="h-11 cursor-pointer border border-[#202020] bg-[#202020] font-roboto-mono text-[13px] font-bold uppercase text-white transition-colors hover:bg-[#2b2b2b]"
        >
          {c.accept[locale]}
        </button>
      </div>
    </div>
  );
}

export function CookieSettingsButton({ className }: { className?: string }) {
  const { openConsentSettings } = useMarketingTracking();
  const { i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? 'ar' : 'en';

  return (
    <button type="button" onClick={openConsentSettings} className={className}>
      {CONSENT_BANNER.settings[locale]}
    </button>
  );
}
